import { countries } from "@redotech/locale/countries";
import {
  FieldId,
  InputAnswerPart,
  InputPartType,
  InputValidation,
  Step,
} from "@redotech/redo-model/return-flow";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import {
  Button,
  ButtonSize,
  ButtonTheme,
  IconButton,
} from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import Camera from "@redotech/redo-web/icon-old/camera.svg";
import {
  default as CloseIcon,
  default as ModalCloseButton,
} from "@redotech/redo-web/icon-old/modal-close-button.svg";
import {
  Dispatch,
  SetStateAction,
  memo,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as input from "./input.module.css";
import * as product from "./product.module.css";

type Answers = { index: number; answer: InputAnswerPart };

export const InputStep = memo(function InputStep({
  step,
  submit,
}: {
  step: Step.Input;
  submit: (answerParts: InputAnswerPart[]) => void;
}) {
  const [inputAnswerParts, setInputAnswerParts] = useState<Answers[]>([]);
  const [openExampleImage, setOpenExampleImage] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setInputAnswerParts([]);
    setOpenExampleImage(false);
  }, [step]);

  const finishStep = () => {
    submit(inputAnswerParts.map((part) => part.answer));
  };

  const disabled = useMemo(() => {
    if (step.parts[0].type === InputPartType.ADDRESS) {
      const requiredIndexes = [0, 2, 3, 4, 5];
      const indexesInArray = inputAnswerParts.map((item) => item.index);
      return !requiredIndexes.every((index) => indexesInArray.includes(index));
    }

    return step.parts.some(
      (part, index) =>
        part.validation === InputValidation.REQUIRED &&
        !inputAnswerParts.find(
          (inputAnswerPart) => inputAnswerPart.index === index,
        ),
    );
  }, [inputAnswerParts, step.parts]);

  return (
    <>
      {openExampleImage && step.parts[0].exampleImage ? (
        <ExampleImagePopup
          exampleImage={step.parts[0].exampleImage}
          setOpenExampleImage={setOpenExampleImage}
        />
      ) : null}

      <div className={input.wrapper}>
        <div className={input.inputSection}>
          <div className={product.questionHeader}>{step.parts[0].message}</div>
          <div className={input.responseSection}>
            {step.parts.map((part, index) => (
              <div key={index}>
                {part.type !== InputPartType.ADDRESS &&
                  part.type !== InputPartType.BOOLEAN && (
                    <div
                      className={
                        part.size === "small"
                          ? product.smallQuestionDescription
                          : product.questionDescription
                      }
                      // TODO parse HTML safely
                      dangerouslySetInnerHTML={{ __html: part.description }}
                    />
                  )}
                {part.type === InputPartType.IMAGES ||
                part.type === InputPartType.VIDEOS ? (
                  <>
                    {step.parts[0].exampleImage && (
                      <div className={product.questionDescription}>
                        See an example of what you should upload{" "}
                        <a
                          className={product.questionDescriptionLink}
                          onClick={() => setOpenExampleImage(true)}
                        >
                          here
                        </a>
                        .
                      </div>
                    )}
                    <ImagePart
                      index={index}
                      inputAnswerParts={inputAnswerParts}
                      inputPartType={part.type}
                      part={part}
                      setInputAnswerParts={setInputAnswerParts}
                      setUploading={setUploading}
                    />
                  </>
                ) : part.type === InputPartType.ADDRESS ? (
                  <AddressPart
                    part={part}
                    setInputAnswerParts={setInputAnswerParts}
                  />
                ) : part.type === InputPartType.BOOLEAN ? (
                  <BooleanPart
                    description={part.description}
                    index={index}
                    inputAnswerParts={inputAnswerParts}
                    part={part}
                    setInputAnswerParts={setInputAnswerParts}
                  />
                ) : (
                  <TextPart
                    index={index}
                    inputAnswerParts={inputAnswerParts}
                    part={part}
                    setInputAnswerParts={setInputAnswerParts}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className={product.button}>
          <Button
            disabled={disabled || uploading}
            onClick={finishStep}
            size={ButtonSize.MEDIUM}
            theme={ButtonTheme.PRIMARY}
            type="submit"
            w="full"
          >
            Continue
          </Button>
        </div>
      </div>
    </>
  );
});

const ExampleImagePopup = memo(function ExampleImagePopup({
  setOpenExampleImage,
  exampleImage,
}: {
  setOpenExampleImage: Dispatch<SetStateAction<boolean>>;
  exampleImage: URL;
}) {
  return (
    <>
      <div
        className={product.conditionBackground}
        onClick={() => setOpenExampleImage(false)}
      />
      <div className={product.conditionCard}>
        <div className={product.cardContent}>
          <img className={product.cardImage} src={exampleImage.toString()} />
        </div>
        <ModalCloseButton
          className={product.closeButtonImage}
          onClick={() => setOpenExampleImage(false)}
        />
      </div>
    </>
  );
});

const ImagePart = memo(function ImagePart({
  inputPartType,
  index,
  part,
  setInputAnswerParts,
  inputAnswerParts,
  setUploading,
}: {
  inputPartType: InputPartType;
  index: number;
  part: Step.InputPart;
  setInputAnswerParts: Dispatch<SetStateAction<Answers[]>>;
  inputAnswerParts: Answers[];
  setUploading: Dispatch<SetStateAction<boolean>>;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<URL[]>([]);

  useEffect(() => {
    // Resets image field - only needed when there are 2 input steps in a row.
    setFiles([]);
    setImageUrls([]);
  }, [part]);

  const handleChange = (images: URL[]) => {
    let tempInputAnswers = [...inputAnswerParts];
    if (tempInputAnswers.length > 0) {
      tempInputAnswers = tempInputAnswers.filter(
        (part) => part.index !== index,
      );
    }
    if (images.length > 0) {
      tempInputAnswers.push({
        index,
        answer: {
          message: part.message,
          value: images,
          type: inputPartType,
          fieldId: part.fieldId,
        },
      });
    }
    setInputAnswerParts(tempInputAnswers);
  };

  const handleUpload: React.ChangeEventHandler<HTMLInputElement> = async (
    event,
  ) => {
    if (!event.target.files?.[0]) {
      return;
    }

    setUploading(true);
    const tempFiles = [...files];
    tempFiles.push(event.target.files[0]);
    setFiles(tempFiles);

    const form = new FormData();
    form.append("image", event.target.files[0]);
    const response = await (window as any).uploadImages(form);
    const url = response.data.url;
    setImageUrls([...imageUrls, url]);
    handleChange([...imageUrls, url]);
    setUploading(false);
  };

  const handleRemove = (index: number) => {
    const tempFiles = [...files];
    tempFiles.splice(index, 1);
    setFiles(tempFiles);

    const tempUrls = [...imageUrls];
    tempUrls.splice(index, 1);
    setImageUrls(tempUrls);
    handleChange(tempUrls);
  };

  const temporaryFileUrl = (file: File) => {
    return URL.createObjectURL(file);
  };

  const clearInput: React.MouseEventHandler<HTMLInputElement> = (e) => {
    // This makes the onChange always get triggered.
    if (e.target instanceof HTMLInputElement) {
      e.target.value = "";
    }
  };

  const fileTypes =
    inputPartType === InputPartType.IMAGES
      ? "image/jpg,image/png,image/webp,image/gif"
      : "video/*";

  return (
    <div className={input.grid}>
      {files.length > 0 &&
        files.map((file, fileIndex) => (
          <div key={fileIndex} style={{ position: "relative" }}>
            <IconButton
              className={input.closeButton}
              onClick={() => handleRemove(fileIndex)}
            >
              <CloseIcon />
            </IconButton>
            {file.type.startsWith("image/") ? (
              <img
                className={input.uploadedImage}
                src={temporaryFileUrl(file)}
              />
            ) : file.type.startsWith("video/") ? (
              <video
                className={input.uploadedImage}
                src={temporaryFileUrl(file)}
              />
            ) : null}
          </div>
        ))}
      <label
        htmlFor={`image-upload-${index}`}
        id={`image-upload-label-${index}`}
      >
        <div className={input.imageButton}>
          <Camera />
        </div>
      </label>
      <input
        accept={fileTypes}
        id={`image-upload-${index}`}
        onChange={(e) => handleUpload(e)}
        onClick={(e) => clearInput(e)}
        required={
          part.validation === InputValidation.REQUIRED && files.length === 0
        }
        style={{ display: "none" }}
        type="file"
      />
    </div>
  );
});

const TextPart = memo(function TextPart({
  setInputAnswerParts,
  index,
  inputAnswerParts,
  part,
}: {
  setInputAnswerParts: Dispatch<SetStateAction<Answers[]>>;
  index: number;
  inputAnswerParts: Answers[];
  part: Step.InputPart;
}) {
  const [description, setDescription] = useState("");

  useEffect(() => {
    // Resets text field - only needed when there are 2 input steps in a row.
    // const test = description;
    setDescription("");
  }, [part]);

  const handleChange = (newDescription: string) => {
    setDescription(newDescription);
    let tempInputAnswerParts = [...inputAnswerParts];
    if (tempInputAnswerParts.length > 0) {
      tempInputAnswerParts = tempInputAnswerParts.filter(
        (part) => part.index !== index,
      );
    }
    if (newDescription && newDescription !== "") {
      tempInputAnswerParts.push({
        index,
        answer: {
          message: part.message,
          value: newDescription,
          type: InputPartType.TEXTAREA,
          fieldId: part.fieldId,
        },
      });
    }
    setInputAnswerParts(tempInputAnswerParts);
  };

  if (part.size === "small") {
    return (
      <input
        autoComplete={part.autoComplete}
        className={
          part.size === "small"
            ? input.smallDescriptionField
            : input.descriptionField
        }
        name={part.inputName}
        onChange={(e) => handleChange(e.target.value)}
        value={description}
      />
    );
  }

  return (
    <textarea
      autoComplete={part.autoComplete}
      className={
        part.size === "small"
          ? input.smallDescriptionField
          : input.descriptionField
      }
      name={part.inputName}
      onChange={(e) => handleChange(e.target.value)}
      value={description}
    />
  );
});

const AddressPart = memo(function AddressPart({
  setInputAnswerParts,
  part,
}: {
  setInputAnswerParts: Dispatch<SetStateAction<Answers[]>>;
  part: Step.InputPart;
}) {
  const [address, setAddress] = useState({
    street1: "",
    street2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setAddress((prevAddress) => ({ ...prevAddress, [name]: value }));
  };

  useEffect(() => {
    setAddress({
      street1: "",
      street2: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    });
  }, [part]);

  useEffect(() => {
    const fields = [
      {
        value: address.street1,
        message: "street",
        index: 0,
        fieldId: FieldId.ACCOUNTS_ADDRESS_STREET_1,
      },
      {
        value: address.street2,
        message: "apartment",
        index: 1,
        fieldId: FieldId.ACCOUNTS_ADDRESS_STREET_2,
      },
      {
        value: address.city,
        message: "city",
        index: 2,
        fieldId: FieldId.ACCOUNTS_ADDRESS_CITY,
      },
      {
        value: address.state,
        message: "state",
        index: 3,
        fieldId: FieldId.ACCOUNTS_ADDRESS_STATE,
      },
      {
        value: address.zip,
        message: "zip",
        index: 4,
        fieldId: FieldId.ACCOUNTS_ADDRESS_ZIP,
      },
      {
        value: address.country,
        message: "country",
        index: 5,
        fieldId: FieldId.ACCOUNTS_ADDRESS_COUNTRY,
      },
    ];

    const tempInputAnswerParts = fields
      .filter((field) => field.value)
      .map((field) => ({
        index: field.index,
        answer: {
          message: field.message,
          value: field.value,
          type: InputPartType.ADDRESS,
          fieldId: field.fieldId,
        },
      }));

    setInputAnswerParts(tempInputAnswerParts);
  }, [address, setInputAnswerParts]);

  return (
    <>
      <div className={product.smallQuestionDescription}>Street</div>
      <input
        autoComplete="address-line1"
        className={input.smallDescriptionField}
        name="street1"
        onChange={handleChange}
        value={address.street1}
      />

      <div className={product.smallQuestionDescription}>
        Apartment, suite, etc. (optional)
      </div>
      <input
        autoComplete="address-line2"
        className={input.smallDescriptionField}
        name="street2"
        onChange={handleChange}
        value={address.street2}
      />

      <div
        style={{ display: "flex", gap: "8px", justifyContent: "space-between" }}
      >
        <div>
          <div className={product.smallQuestionDescription}>City</div>
          <input
            autoComplete="address-level2"
            className={input.smallDescriptionField}
            name="city"
            onChange={handleChange}
            value={address.city}
          />
        </div>
        <div>
          <div className={product.smallQuestionDescription}>State/Province</div>
          <input
            autoComplete="address-level1"
            className={input.smallDescriptionField}
            name="state"
            onChange={handleChange}
            value={address.state}
          />
        </div>
        <div>
          <div className={product.smallQuestionDescription}>Zip/Postal</div>
          <input
            autoComplete="postal-code"
            className={input.smallDescriptionField}
            name="zip"
            onChange={handleChange}
            value={address.zip}
          />
        </div>
      </div>

      <div className={product.smallQuestionDescription}>Country</div>
      <select
        autoComplete="country"
        className={input.smallDescriptionField}
        name="country"
        onChange={handleChange}
        value={address.country}
      >
        <option value="">Select a country</option>
        {countries.map((country) => (
          <option key={country.code} value={country.code}>
            {country.name}
          </option>
        ))}
      </select>
    </>
  );
});

const BooleanPart = memo(function BooleanPart({
  index,
  inputAnswerParts,
  description,
  part,
  setInputAnswerParts,
}: {
  index: number;
  inputAnswerParts: Answers[];
  description: string;
  part: Step.InputPart;
  setInputAnswerParts: Dispatch<SetStateAction<Answers[]>>;
}) {
  const [checked, setChecked] = useState(true);

  const handleChange = (checked: boolean) => {
    setChecked(checked);
    let tempInputAnswerParts = [...inputAnswerParts];
    if (tempInputAnswerParts.length > 0) {
      tempInputAnswerParts = tempInputAnswerParts.filter(
        (part) => part.index !== index,
      );
    }
    tempInputAnswerParts.push({
      index,
      answer: {
        message: part.message,
        value: checked ? "true" : "false",
        type: InputPartType.BOOLEAN,
        fieldId: part.fieldId,
      },
    });
    setInputAnswerParts(tempInputAnswerParts);
  };

  // Initialize the checkbox
  useEffect(() => {
    if (!inputAnswerParts.find((part) => part.index === index)) {
      inputAnswerParts.push({
        index,
        answer: {
          message: part.message,
          value: "true",
          type: InputPartType.BOOLEAN,
          fieldId: part.fieldId,
        },
      });
      setInputAnswerParts(inputAnswerParts);
    }
  }, [inputAnswerParts, part, index, setInputAnswerParts]);

  return (
    <Flex mt="md">
      <RedoCheckbox
        label={description}
        setValue={handleChange}
        size="sm"
        value={checked}
      />
    </Flex>
  );
});
